import { supabase } from './supabase'
import type { SupportLane } from './support'

/**
 * Saved replies for a support lane — 122, HLP-30.
 *
 * A saved reply is a STARTING POINT for the composer: the holder picks one,
 * it lands in the reply box, they edit it, they press Reply. What is sent is
 * `addComment` exactly as if typed. Nothing here sends anything, and nothing
 * here is a note: a note is what a colleague should know about THIS thread,
 * and a template for that is a template for not reading the thread.
 *
 * ── WHO: THE LANE (122's header) ────────────────────────────────────────────
 *
 * Any holder of the lane adds, changes or removes one, including one a
 * colleague wrote. The server verifies that on every call (rule 21); the
 * `lanes.includes(...)` checks in the routes are presentation on top of it.
 * The person a question was HANDED to (119) is not a holder and is offered
 * neither the picker nor the list.
 *
 * ── THE READ TAKES NO LIMIT (rule 1's stated exception) ─────────────────────
 *
 * A lane's templates are a short list and a picker needs all of them, so
 * `support_saved_replies()` returns the lot and the client may group them by
 * lane. It REFUSES a lane you do not hold, and holding none (rule 12).
 */

export type SavedReply = {
  id: string
  lane: SupportLane
  title: string
  body: string
  updatedAt: string
}

/** The token a saved reply may carry for the asker's first name. Substituted
 *  on INSERT into the box, in the client, the one place the name is already
 *  in hand — the server never reads a person for this. */
export const NAME_TOKEN = '{name}'

export async function loadSavedReplies(lane: SupportLane | null = null): Promise<SavedReply[]> {
  const { data, error } = await supabase.rpc('support_saved_replies', { p_lane: lane })
  if (error) throw new Error(error.message)
  return ((data ?? []) as Array<Record<string, unknown>>).map((r) => ({
    id: r.id as string,
    lane: r.lane as SupportLane,
    title: r.title as string,
    body: r.body as string,
    updatedAt: r.updated_at as string,
  }))
}

/** Add (id null) or REPLACE (rule 9: both fields, every time). The lane is
 *  required for a new one and ignored-if-equal on a replace — the server
 *  refuses a lane change. */
export async function saveSavedReply(id: string | null, lane: SupportLane, title: string, body: string): Promise<string> {
  const { data, error } = await supabase.rpc('save_support_reply', {
    p_id: id,
    p_lane: lane,
    p_title: title.trim(),
    p_body: body.trim(),
  })
  if (error) throw new Error(error.message)
  return data as string
}

export async function removeSavedReply(id: string): Promise<void> {
  const { error } = await supabase.rpc('remove_support_reply', { p_id: id })
  if (error) throw new Error(error.message)
}

/** The body as it lands in the box: `{name}` becomes the asker's first name.
 *  With no name (nothing loaded yet, or a withdrawn asker) the token is
 *  dropped along with the space before it, so "Hi {name} —" reads "Hi —"
 *  rather than "Hi {name} —" to a real person. */
export function fillSavedReply(body: string, firstName: string | null | undefined): string {
  const name = (firstName ?? '').trim()
  if (name) return body.split(NAME_TOKEN).join(name)
  return body.replace(/ ?\{name\}/g, '')
}

/** What the composer's draft becomes when a saved reply is inserted: the
 *  reply itself when the box is empty, otherwise appended after a blank line
 *  so nothing already typed is lost. */
export function insertSavedReply(draft: string, filled: string): string {
  const cur = draft.replace(/\s+$/, '')
  return cur ? `${cur}\n\n${filled}` : filled
}

/** The replies of one lane, from the whole list — grouped client-side because
 *  the read took no limit (rule 1's exception, stated in 122). */
export function repliesForLane(all: SavedReply[], lane: SupportLane): SavedReply[] {
  return all.filter((r) => r.lane === lane)
}
